import axios from "axios";
import { store } from "../store";
import { logout, setCredentials } from "../store/authSlice";
import { AuthService } from "./auth.service";
import { PublicRoutePath } from "../config/routes";
import { triggerNativeLogin, triggerNativeLogout } from "../utils/nativeBridge";
import { clearStoragePreservingTours } from "../utils/storage-utils";

const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:3000/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Send cookies with requests
});

// Cache public pages
const flattenRoutes = (routes: Record<string, any>): string[] => {
  return Object.values(routes).reduce((acc: string[], value) => {
    if (typeof value === "string") {
      acc.push(value);
    } else if (typeof value === "object" && value !== null) {
      acc.push(...flattenRoutes(value));
    }
    return acc;
  }, [] as string[]);
};

const publicPages = flattenRoutes(PublicRoutePath)
  .filter((path) => path !== "/")
  .map((path) => new RegExp(path.replace(/:\w+/g, ".*")));

// Function to check if current page is public
const isPublicPage = (): boolean => {
  return publicPages.some((page) => page.test(window.location.pathname));
};

// Helper function for redirecting to login if needed
const redirectToLoginIfNeeded = () => {
  // Full storage clear (same as proper logout) so no stale data lingers
  clearStoragePreservingTours();
  // Notify native shell to clear its SecureStore token too
  triggerNativeLogout();

  // Only redirect if we're not already on the signup page or a public page
  if (!isPublicPage()) {
    window.location.replace("/signup");
  }
};

// Add a request interceptor
api.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const token =
      sessionStorage.getItem("token") ||
      state.auth.token ||
      localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Track if we're currently refreshing the token to prevent multiple refresh requests
let isRefreshing = false;
// Store pending requests that should be retried after token refresh
let pendingRequests: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: any) => void;
  config: any;
}> = [];

// Process all pending requests with the new token
const processPendingRequests = (token: string | null) => {
  pendingRequests.forEach((request) => {
    if (token) {
      request.config.headers.Authorization = `Bearer ${token}`;
      request.resolve(api(request.config));
    } else {
      request.reject(new Error("Token refresh failed"));
    }
  });
  pendingRequests = [];
};

// Add a response interceptor
api.interceptors.response.use(
  async (response) => {
    // Check if backend auto-refreshed the token
    const newAccessToken = response.headers["x-new-access-token"];

    // Parallel requests sent with the same expired token all come back carrying
    // the same refreshed token, so only act on the first one.
    if (newAccessToken && newAccessToken !== localStorage.getItem("token")) {
      const memberStr = localStorage.getItem("member");
      const member = memberStr ? JSON.parse(memberStr) : null;

      let tokenSub: string | null = null;
      try {
        const payload = JSON.parse(atob(newAccessToken.split(".")[1]));
        tokenSub = payload.sub ?? null;
      } catch {
        console.warn("[API] Could not decode auto-refreshed token, discarding");
      }

      if (!tokenSub || !member || tokenSub !== member.id) {
        console.warn(
          "[API] Discarding auto-refreshed token: sub mismatch or no session",
          tokenSub,
          "!=",
          member?.id
        );
      } else {
        console.log(
          "[API] Token auto-refreshed by backend, updating localStorage and headers"
        );

        localStorage.setItem("token", newAccessToken);
        api.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${newAccessToken}`;
        store.dispatch(setCredentials({ token: newAccessToken, member }));

        if (
          typeof window !== "undefined" &&
          (window as any).ReactNativeWebView
        ) {
          triggerNativeLogin(newAccessToken, member);
        }
      }
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and not from auth endpoints and we haven't tried to refresh yet
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/login") &&
      !originalRequest.url?.includes("/auth/logout") &&
      !originalRequest.url?.includes("/auth/refresh-token") &&
      !originalRequest.url?.includes("/register") &&
      !originalRequest.url?.includes("/register/member")
    ) {
      if (!isRefreshing) {
        isRefreshing = true;
        originalRequest._retry = true;

        try {
          // Attempt to refresh the token
          const refreshResponse = await AuthService.refreshToken();
          const newToken = refreshResponse.token;
          const newRefreshToken = refreshResponse.refreshToken;

          // Update tokens in localStorage
          localStorage.setItem("token", newToken);
          if (newRefreshToken) {
            localStorage.setItem("refreshToken", newRefreshToken);
          }

          // Get current member from localStorage
          const memberStr = localStorage.getItem("member");
          const member = memberStr ? JSON.parse(memberStr) : null;

          // Update Redux store with new token
          if (member) {
            store.dispatch(setCredentials({ token: newToken, member }));
          }

          // Update the failed request's Authorization header
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Process any pending requests with the new token
          processPendingRequests(newToken);

          // Retry the original request with new token
          return api(originalRequest);
        } catch (refreshError: any) {
          // Differentiate between auth failures and network/server issues
          const isAuthFailure =
            refreshError.response?.status === 401 ||
            refreshError.response?.status === 403;
          const isNetworkError = !refreshError.response; // No response = network issue
          const isServerError = refreshError.response?.status >= 500;

          // Check if error is due to missing refresh token (old session without cookie)
          // Check both response.data.message and error.message (AuthService re-throws)
          const errorMessage = (
            refreshError.response?.data?.message ||
            refreshError.message ||
            ""
          ).toLowerCase();
          const isMissingRefreshToken = errorMessage.includes("refresh token");
          // Only logout for genuine auth failures or missing refresh token
          if (isAuthFailure || isMissingRefreshToken) {
            console.log(
              "[Auth] Token refresh failed - invalid/missing refresh token, logging out"
            );
            processPendingRequests(null);

            // Clear session storage
            sessionStorage.clear();

            store.dispatch(logout());

            // Redirect to login (with public page protection)
            redirectToLoginIfNeeded();
            return Promise.reject(refreshError);
          }

          // For network/server errors, don't logout - just reject the request
          if (isNetworkError || isServerError) {
            console.warn(
              "[Auth] Token refresh failed due to network/server issue, NOT logging out"
            );
            processPendingRequests(null);
            return Promise.reject(refreshError);
          }

          // For other errors, also don't logout
          console.warn(
            "[Auth] Token refresh failed with unexpected error, NOT logging out"
          );
          processPendingRequests(null);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // If another request is already refreshing, add this request to pending
        return new Promise((resolve, reject) => {
          pendingRequests.push({
            resolve,
            reject,
            config: originalRequest,
          });
        });
      }
    }

    return Promise.reject(error);
  }
);

export default api;
